import { type FC, useMemo } from "react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { formatNumber } from "../lib/format";
import { cn } from "../lib/utils";
import tailwindConfig from "../tailwind-config";
import { Skeleton } from "./skeleton";
import { Typography } from "./typography";

interface ProgressChartProps {
  value: number | null;
  max: number | null;
  loading?: boolean;
  className?: string;
}

const chartFillColor = tailwindConfig.theme.colors.blue[50];
const chartBackgroundColor = tailwindConfig.theme.colors.gray[50];
export const ProgressChart: FC<ProgressChartProps> = ({
  value,
  max,
  loading = true,
  className,
}) => {
  const isEmpty = value === null || max === null || (value === 0 && max === 0);

  const progressPercent = useMemo(() => {
    if (isEmpty) return "-%";

    const percent = (value / max) * 100;

    if (percent < 1 && percent > 0) return "<1%";
    return `${Math.round(percent)}%`;
  }, [value, max, isEmpty]);
  const formattedValue = !isEmpty ? formatNumber(value) : "-";
  const formattedMax = !isEmpty ? formatNumber(max) : "-";

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {" "}
      {loading ? (
        <>
          <Skeleton className="h-[44px] w-[44px] rounded-full mb-2" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
          </div>
          <Skeleton className="h-3 w-full" />
        </>
      ) : (
        <>
          <Typography variant="h4">{progressPercent}</Typography>
          <div className="flex items-center justify-between text-gray-700">
            <Typography>{formattedValue}</Typography>
            <Typography>{formattedMax}</Typography>
          </div>
          <ResponsiveContainer width="100%" height={20}>
            <BarChart
              data={[{ name: "Progress", value }]}
              layout="vertical"
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              key={`${value}-${max}`}
            >
              {max && <XAxis type="number" domain={[0, max]} hide />}
              <YAxis type="category" hide />
              <Bar
                dataKey="value"
                radius={4}
                fill="#7D84F4"
                background={{
                  fill: value && max ? chartFillColor : chartBackgroundColor,
                  radius: 6,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};
